import React, {ForwardedRef, forwardRef, ReactElement, useEffect} from "react";
import WebFont from "webfontloader";
import {BrandStylesResult} from "data/models/deliverables/brand-styles";
import {CopyToClipboardButton} from "components/CopyToClipboardButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCopy} from "@fortawesome/free-solid-svg-icons";

type BrandStylesProps = {
  brandStyles: BrandStylesResult;
  businessName: string;
  brandVoice: string;
}


const BrandStylesPreview = forwardRef<HTMLDivElement, BrandStylesProps>((
    {
      brandStyles,
      businessName,
      brandVoice,
    }: BrandStylesProps,
    ref: ForwardedRef<HTMLDivElement>
) => {

  const {
    brandColors,
    brandFont,
    subtitleFont,
    headingFont,
    subheadingFont,
    sectionHeaderFont,
    bodyFont,
    quoteFont,
    captionFont,
  } = brandStyles;

  useEffect(() => {
    WebFont.load({
      google: {
        families: [...new Set([
          brandFont.family,
          subtitleFont.family,
          headingFont.family,
          subheadingFont.family,
          sectionHeaderFont.family,
          bodyFont.family,
          quoteFont.family,
          captionFont.family,
        ])],
      },
    });
  }, [
    brandFont,
    subtitleFont,
    headingFont,
    subheadingFont,
    sectionHeaderFont,
    bodyFont,
    quoteFont,
    captionFont,
  ]);

  const CopyButton = ({handleCopy}: {
    handleCopy: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  }): ReactElement => <a
      className={"cursor-pointer"}
      color={"blue"}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        handleCopy(event);
      }}
  >
    <FontAwesomeIcon icon={faCopy} className={"ml-4 text-bc-blue"} size={"sm"}/>
  </a>;

  const sectionHeaderStyle = {
    fontFamily: sectionHeaderFont.name,
    fontWeight: sectionHeaderFont.weight ?? 400,
    fontStyle: sectionHeaderFont.italic ? "italic" : "normal",
  };

  const captionStyle = {
    fontFamily: captionFont.name,
    fontWeight: captionFont.weight ?? 400,
    fontStyle: captionFont.italic ? "italic" : "normal",
  };
  const colorCodesStyle = {
    ...captionStyle,
    fontWeight: 400,
  };
  const bodyStyle = {
    fontFamily: bodyFont.name,
    fontWeight: bodyFont.weight ?? 400,
    fontStyle: bodyFont.italic ? "italic" : "normal",
  };

  return (
      <div
          className={"flex mt-12 screen:px-8 screen:pt-8 screen:pb-12 flex-row border-3 rounded-xl border-neutral-50 mx-auto"}>
        <div ref={ref} className={"w-full flex flex-col text-left print:my-6 print:px-16 print:max-w-screen-lg"}>
          <div
              className={"text-2xl text-center font-normal"}
              style={{
                fontFamily: brandFont.name,
                color: brandColors.primary.color,
              }}
          >{businessName} Brand Styles
          </div>
          <div className={"mt-4"}>
            <span
                className={"w-full text-section-header"}
                style={sectionHeaderStyle}>Fonts</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-title"}
                style={{
                  fontFamily: brandFont.name,
                  fontWeight: brandFont.weight ?? 400,
                  fontStyle: brandFont.italic ? "italic" : "normal",
                  color: brandColors.primary.color,
                }}>Title: {brandFont.name} 72pt Bold</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-sub-title"}
                style={{
                  fontFamily: subtitleFont.name,
                  fontWeight: subtitleFont.weight ?? 400,
                  fontStyle: subtitleFont.italic ? "italic" : "normal",
                  color: brandColors.secondary.color,
                }}>Subtitle: {subtitleFont.name} 64pt bold</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-heading"}
                style={{
                  fontFamily: headingFont.name,
                  fontWeight: headingFont.weight ?? 400,
                  fontStyle: headingFont.italic ? "italic" : "normal",
                  color: brandColors.primary.color,
                }}>Heading: {headingFont.name} 56pt bold</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-sub-heading"}
                style={{
                  fontFamily: subheadingFont.name,
                  fontWeight: subheadingFont.weight ?? 400,
                  fontStyle: subheadingFont.italic ? "italic" : "normal",
                  color: brandColors.secondary.color,
                }}>Subheading: {subheadingFont.name} 48pt bold</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-section-header"}
                style={sectionHeaderStyle}>Section header: {sectionHeaderFont.name} 32pt bold</span>
          </div>
          <div>
            <span
                className={"w-full text-body"}
                style={bodyStyle}>Body: {bodyFont.name} 16pt</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-quote"}
                style={{
                  fontFamily: quoteFont.name,
                  fontWeight: quoteFont.weight ?? 400,
                  fontStyle: quoteFont.italic ? "italic" : "normal",
                }}>Quote: {quoteFont.name} 40pt</span>
          </div>
          <div className={"w-full print:text-nowrap print:overflow-ellipsis print:overflow-hidden"}>
            <span
                className={"w-full text-caption"}
                style={captionStyle}>Caption: {captionFont.name} 16pt bold</span>
          </div>
          <div className={"mt-8"}>
            <span
                className={"w-full text-section-header"}
                style={sectionHeaderStyle}>Colors</span>
          </div>
          <div className={"flex flex-row"}>
            <div className={"w-1/2 mr-2 flex flex-col"}>
              <div><span
                  className={"w-full text-caption"}
                  style={captionStyle}>PRIMARY</span></div>
              <div
                  className={"w-full h-48"}
                  style={{backgroundColor: brandColors.primary.color}}
              ></div>
              <div><span
                  className={"w-full text-caption"}
                  style={colorCodesStyle}>{brandColors.primary.color.toUpperCase()}</span></div>
            </div>
            <div className={"w-1/4 mx-2 flex flex-col ml"}>
              <div><span
                  className={"w-full text-caption"}
                  style={captionStyle}>SECONDARY</span></div>
              <div
                  className={"w-full h-48"}
                  style={{backgroundColor: brandColors.secondary.color}}
              ></div>
              <div><span
                  className={"w-full text-caption"}
                  style={colorCodesStyle}>{brandColors.secondary.color.toUpperCase()}</span></div>
            </div>
            <div className={"w-1/4 ml-2 flex flex-col"}>
              <div><span
                  className={"w-full text-caption"}
                  style={captionStyle}>BACKGROUND</span></div>
              <div
                  className={"w-full h-24"}
                  style={{backgroundColor: brandColors.background.color}}
              ></div>
              <div><span
                  className={"w-full text-caption"}
                  style={colorCodesStyle}>{brandColors.background.color.toUpperCase()}</span></div>
              <div className={"grow flex flex-col justify-end"}>
                <div><span
                    className={"w-full text-caption"}
                    style={captionStyle}>ACCENT</span></div>
                <div
                    className={"w-full h-12"}
                    style={{backgroundColor: brandColors.accent.color}}
                ></div>
                <div><span
                    className={"w-full text-caption"}
                    style={colorCodesStyle}>{brandColors.accent.color.toUpperCase()}</span></div>
              </div>
            </div>
          </div>
          <div className={"flex flex-row mt-8"}>
            <div className={"my-auto"}>
              <span
                  className={"w-full text-section-header"}
                  style={sectionHeaderStyle}>Brand Voice</span>
            </div>
            <div className={"my-auto print:hidden"}>
              <CopyToClipboardButton text={brandVoice} CopyButton={CopyButton}/>
            </div>
          </div>
          <div
              className={"mt-2 overflow-scroll text-body"}
              style={bodyStyle}
          >
            {brandVoice}
          </div>

          <div className={"mt-8 flex flex-row justify-between"}>
            <div className={"w-1/6"}>
              <img src={"/brandingcompass-r-logo.png"} alt={"Branding Compass"}/>
            </div>
            <div className={"text-2xs md:text-xs align-bottom mt-auto"}>
              <a href={"https://www.brandingcompass.com"} target={"_blank"} rel={"noreferrer"}>BrandingCompass.com</a>
              <span className={"ml-2 text-gray-500"}>&copy;{new Date().getFullYear()} Visible Logic, Inc.</span>
            </div>
          </div>
        </div>
      </div>
  )
});

export default BrandStylesPreview;
